import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReservationPaxGroup, ServiceBooking } from '@turizi/shared';
import { Button, Checkbox, TextInput, ActionIcon } from '@mantine/core';
import {
    IconUsersGroup,
    IconSearch,
    IconX,
    IconCheck,
    IconLinkPlus,
} from '@tabler/icons-react';
import CardModal from '../card-modal';
import { useBulkAssignPaxGroupsToBookings } from '@/hooks/api-hooks';
import { ShowPaxGroup } from '../snippets';
import { ServiceTitleStatic } from '../services/service-title';
import { formatDayShort } from '@/lib/format-utils';
import { notify } from '@/lib/notify';

interface PaxGroupAssignBookingsModalProps {
    opened: boolean;
    onClose: () => void;
    reservationId: number;
    group: ReservationPaxGroup | null;
    bookings: ServiceBooking[];
}

export default function PaxGroupAssignBookingsModal({
    opened,
    onClose,
    reservationId,
    group,
    bookings,
}: PaxGroupAssignBookingsModalProps) {
    const { t } = useTranslation();
    const bulkAssignMutation = useBulkAssignPaxGroupsToBookings();

    const [selectedBookingIds, setSelectedBookingIds] = useState<Set<number>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');

    const eligibleBookings = useMemo(() => {
        return (bookings || []).filter(
            (b): b is ServiceBooking & { id: number } =>
                Boolean(b.id) && !(b.service_data?.child_services?.length > 0)
        );
    }, [bookings]);

    useEffect(() => {
        if (!opened || !group?.id) return;
        setSearchQuery('');
        setSelectedBookingIds(new Set());
    }, [opened, group?.id]);

    const filteredBookings = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return eligibleBookings;
        return eligibleBookings.filter((b) => {
            const title = b.service_data ? ServiceTitleStatic(b.service_data).toLowerCase() : '';
            const idStr = `#${b.id}`;
            const dateStr = b.start_day ? formatDayShort(b.start_day).toLowerCase() : '';
            return title.includes(q) || idStr.includes(q) || dateStr.includes(q);
        });
    }, [eligibleBookings, searchQuery]);

    const toggleBooking = (bookingId: number) => {
        setSelectedBookingIds((prev) => {
            const next = new Set(prev);
            if (next.has(bookingId)) {
                next.delete(bookingId);
            } else {
                next.add(bookingId);
            }
            return next;
        });
    };

    const handleSelectAll = (select: boolean) => {
        if (select) {
            setSelectedBookingIds(new Set(eligibleBookings.map((b) => b.id)));
        } else {
            setSelectedBookingIds(new Set());
        }
    };

    const handleSave = async () => {
        if (!group?.id) return;

        const targetBookings = eligibleBookings.filter((b) =>
            selectedBookingIds.has(b.id)
        );

        if (targetBookings.length === 0) {
            onClose();
            return;
        }

        const hasOrders = targetBookings.some(
            (b) => (b.service_order_bookings?.length || 0) > 0
        );
        if (hasOrders && !window.confirm(t('confirm_modify_booking_with_service_order'))) {
            return;
        }

        const assignments = targetBookings.map((b) => ({
            service_booking_id: b.id,
            pax_group_id: group.id!,
        }));

        try {
            await bulkAssignMutation.mutateAsync({
                reservationId,
                assignments,
            });
            notify.success(t('success'), t('pax_distribution_saved'));
            onClose();
        } catch (err) {
            console.error('Error assigning pax group to bookings:', err);
            notify.error(t('error'), t('error'));
        }
    };

    if (!group) return null;

    return (
        <CardModal
            opened={opened}
            onClose={onClose}
            size="680px"
            header={
                <div className="flex items-center gap-2 pr-4 flex-wrap">
                    <IconLinkPlus size={18} className="text-indigo-600" />
                    <span className="font-bold text-base text-slate-800">
                        {t('assign_to_booking')}
                    </span>
                    <div className="flex items-center gap-1.5 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200">
                        <IconUsersGroup size={14} className="text-indigo-600" />
                        <span className="font-semibold text-xs text-slate-800">{group.name}</span>
                        <ShowPaxGroup
                            size="xs"
                            adults={group.adults}
                            children={group.children}
                            seniors={group.seniors}
                            infants={group.infants}
                            guests={group.guests}
                        />
                    </div>
                </div>
            }
            footer={
                <div className="w-full flex items-center justify-between gap-2">
                    <div className="text-xs text-slate-500 font-medium">
                        {t('target_bookings_count', { count: selectedBookingIds.size })}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="subtle"
                            color="black"
                            size="xs"
                            onClick={onClose}
                            disabled={bulkAssignMutation.isPending}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            leftSection={<IconCheck size={14} />}
                            loading={bulkAssignMutation.isPending}
                            disabled={selectedBookingIds.size === 0}
                            onClick={handleSave}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs text-slate-500">
                        {t('select_bookings_to_assign')}
                    </div>
                    <div className="flex items-center gap-1.5 ml-auto">
                        {eligibleBookings.length > 4 && (
                            <TextInput
                                size="xs"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.currentTarget.value)}
                                placeholder={t('search')}
                                leftSection={<IconSearch size={12} className="text-slate-400" />}
                                rightSection={
                                    searchQuery ? (
                                        <ActionIcon
                                            size="xs"
                                            variant="subtle"
                                            color="gray"
                                            onClick={() => setSearchQuery('')}
                                        >
                                            <IconX size={10} />
                                        </ActionIcon>
                                    ) : null
                                }
                                className="w-44"
                            />
                        )}
                        <Button
                            size="compact-xs"
                            variant="subtle"
                            color="gray"
                            onClick={() => handleSelectAll(true)}
                            disabled={selectedBookingIds.size === eligibleBookings.length}
                        >
                            {t('all')}
                        </Button>
                        <Button
                            size="compact-xs"
                            variant="subtle"
                            color="gray"
                            onClick={() => handleSelectAll(false)}
                            disabled={selectedBookingIds.size === 0}
                        >
                            {t('deselect_all')}
                        </Button>
                    </div>
                </div>

                <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
                    <div className="max-h-[56vh] overflow-y-auto subtle-scrollbar divide-y divide-slate-100">
                        {filteredBookings.length === 0 ? (
                            <div className="p-6 text-center text-xs text-slate-400">
                                {t('no_results')}
                            </div>
                        ) : (
                            filteredBookings.map((booking) => {
                                const isSelected = selectedBookingIds.has(booking.id);
                                return (
                                    <div
                                        key={booking.id}
                                        onClick={() => toggleBooking(booking.id)}
                                        className={`px-3 py-2.5 flex items-center justify-between gap-3 cursor-pointer transition-colors select-none ${isSelected
                                            ? 'bg-emerald-50/70 hover:bg-emerald-50'
                                            : 'bg-white hover:bg-slate-50'
                                            }`}
                                    >
                                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                            <Checkbox
                                                size="xs"
                                                color="black"
                                                checked={isSelected}
                                                onChange={() => { }}
                                                className="pointer-events-none shrink-0"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-800 truncate">
                                                    <span className="px-1.5 py-0.5 rounded bg-slate-200/80 text-slate-700 text-[10px] font-semibold tabular-nums shrink-0">
                                                        #{booking.id}
                                                    </span>
                                                    <span className="truncate">
                                                        {ServiceTitleStatic(booking.service_data)}
                                                    </span>
                                                </div>
                                                {booking.start_day && (
                                                    <div className="text-[11px] text-slate-500 mt-0.5">
                                                        {formatDayShort(booking.start_day)}
                                                        {booking.end_day && booking.end_day !== booking.start_day
                                                            ? ` → ${formatDayShort(booking.end_day)}`
                                                            : ''}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="shrink-0">
                                            <ShowPaxGroup
                                                size="xs"
                                                adults={booking.adults}
                                                children={booking.children}
                                                seniors={booking.seniors}
                                                infants={booking.infants}
                                                guests={booking.guests}
                                                showRetired={Boolean(booking.service_data?.show_retired)}
                                            />
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        </CardModal>
    );
}

